// Shared client-side filtering for settlement details, reused by the
// Settlement Detail page and the printable Settlement Report.

import { encryptForUrl, decryptForUrl } from "../../../common/utils";

export const EMPTY_SETTLEMENT_FILTERS = {
  cities: [],
  states: [],
  personalIds: [],
  eventIds: [],
  paymentTypes: [],
};

export const getPaymentTypeName = (item) =>
  item?.paymentType?.Name ??
  item?.additionalCostType?.paymentType?.Name ??
  null;

// Build distinct, non-empty option lists from the loaded details.
export const getSettlementFilterOptions = (details) => {
  const cities = new Map();
  const states = new Map();
  const personals = new Map();
  const events = new Map();
  const paymentTypes = new Map();

  (details ?? []).forEach((item) => {
    const cityName = item?.event?.City?.Name;
    if (cityName) cities.set(cityName, cityName);

    const stateName = item?.event?.State?.Name;
    if (stateName) states.set(stateName, stateName);

    const personalId = item?.personal?.id;
    if (personalId != null && !personals.has(personalId)) {
      personals.set(personalId, {
        id: personalId,
        name: item?.personal?.FullName ?? `${personalId}`,
      });
    }

    const eventId = item?.event?.Id;
    if (eventId != null && !events.has(eventId)) {
      events.set(eventId, { id: eventId, code: item?.event?.Code ?? `${eventId}` });
    }

    const paymentTypeName = getPaymentTypeName(item);
    if (paymentTypeName) paymentTypes.set(paymentTypeName, paymentTypeName);
  });

  return {
    cities: Array.from(cities.values()),
    states: Array.from(states.values()),
    personals: Array.from(personals.values()),
    events: Array.from(events.values()),
    paymentTypes: Array.from(paymentTypes.values()),
  };
};

// Apply filters: OR within a field, AND across fields. Empty array = no constraint.
export const filterSettlementDetails = (details, filters) => {
  const f = { ...EMPTY_SETTLEMENT_FILTERS, ...(filters ?? {}) };
  return (details ?? []).filter((item) => {
    if (f.cities.length && !f.cities.includes(item?.event?.City?.Name)) {
      return false;
    }
    if (f.states.length && !f.states.includes(item?.event?.State?.Name)) {
      return false;
    }
    if (
      f.personalIds.length &&
      !f.personalIds.includes(item?.personal?.id)
    ) {
      return false;
    }
    if (f.eventIds.length && !f.eventIds.includes(item?.event?.Id)) {
      return false;
    }
    if (
      f.paymentTypes.length &&
      !f.paymentTypes.includes(getPaymentTypeName(item))
    ) {
      return false;
    }
    return true;
  });
};

export const hasActiveSettlementFilters = (filters) => {
  const f = filters ?? {};
  return (
    (f.cities?.length ?? 0) +
      (f.states?.length ?? 0) +
      (f.personalIds?.length ?? 0) +
      (f.eventIds?.length ?? 0) +
      (f.paymentTypes?.length ?? 0) >
    0
  );
};

// Serialize active filters into a single encrypted query param, matching the
// approach used by the event report (encryptForUrl / decryptForUrl).
export const buildSettlementFilterQuery = (filters) => {
  if (!hasActiveSettlementFilters(filters)) return "";
  const compact = { ...EMPTY_SETTLEMENT_FILTERS, ...(filters ?? {}) };
  return `filters=${encryptForUrl(JSON.stringify(compact))}`;
};

// Read + decrypt filters back from a URLSearchParams instance.
export const parseSettlementFilterParams = (searchParams) => {
  const raw = searchParams?.get("filters");
  if (!raw) return { ...EMPTY_SETTLEMENT_FILTERS };
  try {
    const parsed = JSON.parse(decryptForUrl(raw)) ?? {};
    return {
      cities: parsed.cities ?? [],
      states: parsed.states ?? [],
      personalIds: parsed.personalIds ?? [],
      eventIds: parsed.eventIds ?? [],
      paymentTypes: parsed.paymentTypes ?? [],
    };
  } catch (e) {
    return { ...EMPTY_SETTLEMENT_FILTERS };
  }
};
